import {
  GIPHY_API_KEY,
  GIPHY_SEARCH_URL,
  GIPHY_TRENDING_URL,
  RESULT_LIMIT,
  type Rating,
} from '../config';
import type { GifPage, GiphyResponse } from '../types';

export class GiphyError extends Error {}

interface FetchOpts {
  rating: Rating;
  offset?: number;
  limit?: number;
}

async function request(url: string, params: URLSearchParams): Promise<GifPage> {
  params.set('api_key', GIPHY_API_KEY);

  let res: Response;
  try {
    res = await fetch(`${url}?${params.toString()}`);
  } catch {
    throw new GiphyError('Network error — check your connection and try again.');
  }

  if (!res.ok) {
    throw new GiphyError(`Giphy request failed (HTTP ${res.status}).`);
  }

  const body = (await res.json()) as GiphyResponse;
  if (body.meta && body.meta.status >= 400) {
    throw new GiphyError(body.meta.msg || 'Giphy returned an error.');
  }

  return {
    gifs: body.data ?? [],
    totalCount: body.pagination?.total_count ?? body.data?.length ?? 0,
    offset: body.pagination?.offset ?? 0,
  };
}

/** Search Giphy for a term. Fixes the original app's broken `limit` query. */
export function searchGifs(query: string, opts: FetchOpts): Promise<GifPage> {
  const params = new URLSearchParams({
    q: query,
    limit: String(opts.limit ?? RESULT_LIMIT),
    offset: String(opts.offset ?? 0),
    rating: opts.rating,
    bundle: 'messaging_non_clips',
  });
  return request(GIPHY_SEARCH_URL, params);
}

/** Trending GIFs — shown on initial load so the page is never empty. */
export function trendingGifs(opts: FetchOpts): Promise<GifPage> {
  const params = new URLSearchParams({
    limit: String(opts.limit ?? RESULT_LIMIT),
    offset: String(opts.offset ?? 0),
    rating: opts.rating,
    bundle: 'messaging_non_clips',
  });
  return request(GIPHY_TRENDING_URL, params);
}
